import React, { useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import { useI18n } from '../i18n';
import BrandMark from '../components/BrandMark';
import RecordList from '../components/RecordList';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ThemeToggle from '../components/ThemeToggle';
import { IconJobs, IconProperties, IconTeam, IconMore, IconChevron, IconPlus, IconSearch, IconInvoices } from '../components/PortalIcons';
import ProjectStatement from '../components/ProjectStatement';
import { fmtMoney, fmtDate } from '../lib/format';
import DocumentList from '../components/DocumentList';
import { getPayLink } from '../lib/payLink';

// The Commercial Accounts portal — property managers, HOAs, realtors, investors.
//
// Built like the technician portal: one screen, a fixed bottom tab bar, and an
// `accountView` state deciding what sits above it. That layout already works on
// a phone in a van and there is no reason for a fourth pattern.
//
// What's different is scale. A homeowner has one job at one address; a
// management company has four hundred addresses and a standing list of work. So
// Properties is a searched, paginated list rather than a field on a form, and
// raising a work order starts by asking WHICH property instead of asking
// someone to type an address they have already given us.

// Status → the chip RecordList draws. format.js's statusTag() renders finished
// JSX, which is right for a card but not for RecordList's { chipLabel, chipTone }
// contract, so the same mapping is expressed here as data.
const STATUS_TONE = {
  'new': 'slate',
  'estimate-sent': 'amber',
  'approved': 'blue',
  'assigned': 'teal',
  'in-progress': 'blue',
  'completed': 'green',
  'invoiced': 'amber',
  'paid': 'green',
};

// Which segment a work order belongs in.
//
// "Needs you" is the important one and is deliberately not a status: it is
// anything waiting on the management company rather than on us — an estimate to
// answer, or one that came in over the limit. That is the list they should open
// the app to, so it gets its own segment instead of being buried inside Open.
// Off to the pay page for one of the company's statements. Same helper the
// homeowner portal uses — a bill is a bill, whoever is looking at it.
async function goPay({ projectId, token, auth }) {
  try {
    window.location.href = await getPayLink({ projectId, token, auth });
  } catch (err) {
    alert(err.response?.data?.message || 'The pay page could not be opened. Please try again.');
  }
}

const needsThem = (r) => r.estimateStatus === 'pending' || r.nteStatus === 'exceeded';
const isDone = (r) => ['completed', 'invoiced', 'paid'].includes(r.status);

export default function AccountDashboard() {
  const {
    currentScreen, goTo, currentUser, handleLogout,
    accountView, setAccountView,
    account, accountMembers, requests, properties, propertyTotal,
    propertySearch, setPropertySearch, loadProperties,
    openPropertyForm, openPropertyImport, openAccountWorkOrder, openProperty,
    openRequestDetail, openMemberForm, saveAccountSettings, setModal,
    projects, authHeaders, openStatementInvoice,
  } = useApp();
  const { t } = useI18n();

  const isOwner = account?.myLevel === 'owner';
  const [segment, setSegment] = useState('open');

  // Only this account's work orders ever reach the browser — the server filters
  // by account before anything leaves it — so everything here is presentation,
  // never access control.
  const byId = useMemo(() => {
    const m = new Map();
    (requests || []).forEach((r) => m.set(r._id, r));
    return m;
  }, [requests]);

  const rows = useMemo(() => (requests || []).map((r) => ({
    id: r._id,
    name: [r.serviceAddress, r.unitLabel].filter(Boolean).join(' '),
    // Priority rides in the meta line rather than as a second chip: RecordList
    // gives a row one chip, and "over limit" is the more urgent of the two.
    meta: [
      r.priority && r.priority !== 'routine' ? t(`acct.priority.${r.priority}`) : '',
      r.serviceType,
    ].filter(Boolean).join(' · '),
    number: r.workOrder?.number,
    date: r.createdAt,
    amount: r.estimate?.amount,
    chipLabel: r.nteStatus === 'exceeded' ? t('acct.overLimit') : t(`status.${r.status}`, r.status),
    chipTone: r.nteStatus === 'exceeded' ? 'declined' : STATUS_TONE[r.status] || 'slate',
    onClick: () => openRequestDetail(r),
  })), [requests, t, openRequestDetail]);

  const segments = [
    { key: 'needs', label: t('acct.segNeedsYou'), match: (row) => needsThem(byId.get(row.id) || {}) },
    { key: 'open', label: t('acct.segOpen'), match: (row) => !isDone(byId.get(row.id) || {}) },
    { key: 'done', label: t('acct.segDone'), match: (row) => isDone(byId.get(row.id) || {}) },
  ];

  const TABS = [
    { key: 'jobs', label: t('acct.tabJobs'), Icon: IconJobs, dot: (requests || []).some(needsThem) },
    { key: 'properties', label: t('acct.tabProperties'), Icon: IconProperties },
    // What they owe, and a way to pay it. A property manager promoted from
    // an ordinary client arrives here with a statement already running, and
    // a bill they cannot see is a bill they cannot pay.
    { key: 'billing', label: 'Billing', Icon: IconInvoices,
      dot: (projects || []).some(p => (Number(p.owedByClient) || 0) > 0.004) },
    { key: 'team', label: t('acct.tabTeam'), Icon: IconTeam },
    { key: 'more', label: t('tp.more'), Icon: IconMore },
  ];

  return (
    <div className={`screen ${currentScreen === 'screen-account-dashboard' ? 'active' : ''}`}>
      <nav className="nav">
        <BrandMark dark />
        <div className="nav-right" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: 'var(--border)', fontSize: 13 }}>{account?.name || currentUser?.name}</span>
          <ThemeToggle /><LanguageSwitcher dark />
        </div>
      </nav>

      <div className="tp-body">
        {/* ── Jobs ─────────────────────────────────────────────── */}
        {accountView === 'jobs' && (
          <RecordList
            title={t('acct.tabJobs')}
            rows={rows}
            segments={segments}
            segment={segment}
            onSegment={setSegment}
            searchPlaceholder={t('acct.searchJobs')}
            onAdd={() => openAccountWorkOrder(null)}
            addLabel={t('acct.newServiceRequest')}
            emptyTitle={t('acct.noJobsTitle')}
            emptyText={t('acct.noJobsText')}
          />
        )}

        {/* ── Properties ───────────────────────────────────────── */}
        {accountView === 'properties' && (
          <>
            <div className="tp-head">
              <div className="tp-head-row">
                <div style={{ width: 34, flexShrink: 0 }} />
                <div className="tp-title">{t('acct.tabProperties')}</div>
                <div style={{ width: 34, flexShrink: 0 }}>
                  <button className="tp-act tp-act-plus" onClick={() => openPropertyForm(null)} aria-label={t('acct.addProperty')}>
                    <IconPlus size={23} />
                  </button>
                </div>
              </div>

              {/* Searched on the SERVER, not filtered in the browser. Four
                  hundred properties is the opening position, not the ceiling,
                  and shipping a whole portfolio to a phone to filter it there is
                  how this gets slow the month they take on a second one. */}
              <div className="tp-search">
                <IconSearch />
                <input
                  type="search"
                  value={propertySearch}
                  onChange={(e) => { setPropertySearch(e.target.value); loadProperties(e.target.value); }}
                  placeholder={t('acct.searchProperties')}
                  aria-label={t('acct.searchProperties')}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, paddingBottom: 12 }}>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                  {t('acct.propertiesCount').replace('{n}', propertyTotal ?? (properties || []).length)}
                </span>
                <button className="btn btn-outline btn-sm" onClick={openPropertyImport}>{t('acct.import')}</button>
              </div>
            </div>

            {(properties || []).length === 0 ? (
              <div className="tp-empty">
                <div className="tp-empty-t">{propertySearch ? t('tp.noMatches') : t('acct.noPropsTitle')}</div>
                <div>{propertySearch ? t('tp.noMatchesSub') : t('acct.noPropsText')}</div>
                {!propertySearch && (
                  <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={openPropertyImport}>
                    {t('acct.importCta')}
                  </button>
                )}
              </div>
            ) : (
              <div className="tp-rows">
                {(properties || []).map((p) => {
                  const live = (p.units || []).filter((u) => u.isActive !== false);
                  return (
                    <button key={p._id} className="tp-row" onClick={() => openProperty(p)}>
                      <div className="tp-row-main">
                        <div className="tp-row-name">{p.addressLine1}{p.addressLine2 ? ` ${p.addressLine2}` : ''}</div>
                        <div className="tp-row-meta">
                          {[p.label, [p.city, p.state].filter(Boolean).join(', '), p.zip].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      <div className="tp-row-right">
                        {live.length > 0 && (
                          <span className="tp-chip tp-chip-draft">{t('acct.unitCount').replace('{n}', live.length)}</span>
                        )}
                        <IconChevron />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ── Team ─────────────────────────────────────────────── */}
        {accountView === 'billing' && (
          <>
            <div className="tp-head">
              <div className="tp-head-row">
                <div style={{ width: 34 }} />
                <div className="tp-title">Billing</div>
                <div style={{ width: 34 }} />
              </div>
            </div>
            {(projects || []).length === 0 ? (
              <div className="tp-empty">
                <div className="tp-empty-t">Nothing billed yet</div>
                <div>Every invoice QPS Hub raises for your properties shows here, with what has been
                  paid against it and what is left.</div>
              </div>
            ) : (
              <div style={{ padding: '0 16px 16px' }}>
                {projects.map(pr => (
                  <div key={pr._id} style={{ marginBottom: 22 }}>
                    <div className="sec-label" style={{ marginBottom: 8 }}>{pr.title}</div>
                    {pr.address && (
                      <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 8 }}>{pr.address}</div>
                    )}
                    {/* A property manager reads the same statement a
                        homeowner does, and asks the same question of a line
                        on it. */}
                    <ProjectStatement
                      project={pr}
                      onOpenInvoice={(line) => openStatementInvoice(pr, line)}
                      onPay={(p) => goPay({ projectId: p._id, token: p.payToken, auth: authHeaders() })}
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {accountView === 'team' && (
          <>
            <div className="tp-head">
              <div className="tp-head-row">
                <div style={{ width: 34, flexShrink: 0 }} />
                <div className="tp-title">{t('acct.tabTeam')}</div>
                <div style={{ width: 34, flexShrink: 0 }}>
                  {isOwner && (
                    <button className="tp-act tp-act-plus" onClick={() => openMemberForm(null)} aria-label={t('acct.invite')}>
                      <IconPlus size={23} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div style={{ padding: '14px 16px 0' }}>
              {/* Levels are spelled out rather than named and left to be guessed.
                  An owner picking "Coordinator" for a new hire should know that
                  means they cannot approve money. */}
              <div className="alert alert-blue" style={{ fontSize: 13, marginBottom: 4 }}>
                {t('acct.levelsExplained')}
              </div>
            </div>

            <div className="tp-rows" style={{ marginTop: 12 }}>
              {(accountMembers || []).map((m) => (
                <button
                  key={m.userId}
                  className="tp-row"
                  onClick={() => { if (isOwner && !m.isMe) openMemberForm(m); }}
                  disabled={!isOwner || m.isMe}
                  style={!isOwner || m.isMe ? { cursor: 'default' } : undefined}
                >
                  <div className="tp-row-main">
                    <div className="tp-row-name">{m.name}{m.isMe ? ` · ${t('acct.you')}` : ''}</div>
                    <div className="tp-row-meta">{m.email}</div>
                  </div>
                  <div className="tp-row-right">
                    <span className={`tag tag-${m.level === 'owner' ? 'navy' : m.level === 'manager' ? 'blue' : 'slate'}`}>
                      {t(`acct.level.${m.level}`)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {/* ── More ─────────────────────────────────────────────── */}
        {accountView === 'more' && (
          <>
            <div className="tp-head">
              <div className="tp-head-row">
                <div style={{ width: 34 }} />
                <div className="tp-title">{t('tp.more')}</div>
                <div style={{ width: 34 }} />
              </div>
            </div>

            <div style={{ padding: '18px 16px 0' }}>
              <div className="sec-label">{account?.name || ''}</div>

              {isOwner ? (
                <>
                  <div className="form-group">
                    <label>{t('acct.defaultNte')}</label>
                    {/* Saved on blur rather than behind a Save button: it is one
                        number, and a settings screen that loses what you typed
                        because you tapped away is the more annoying failure. */}
                    <input
                      type="number" inputMode="decimal" min="0"
                      defaultValue={account?.defaultNteAmount ?? ''}
                      placeholder={t('acct.noLimitSet')}
                      onBlur={(e) => saveAccountSettings({ defaultNteAmount: e.target.value === '' ? null : e.target.value })}
                    />
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6, lineHeight: 1.5 }}>
                      {t('acct.defaultNteHelp')}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>{t('acct.billingEmail')}</label>
                    <input
                      type="email"
                      defaultValue={account?.billingEmail || ''}
                      onBlur={(e) => saveAccountSettings({ billingEmail: e.target.value })}
                    />
                  </div>
                </>
              ) : (
                <div className="vsheet" style={{ padding: '0 0 14px' }}>
                  <div className="vsheet-row">
                    <div className="vsheet-label">{t('acct.defaultNte')}</div>
                    <div className="vsheet-value">
                      {account?.defaultNteAmount != null ? fmtMoney(account.defaultNteAmount) : t('acct.noLimitSet')}
                    </div>
                  </div>
                  <div className="vsheet-row">
                    <div className="vsheet-label">{t('acct.yourLevel')}</div>
                    <div className="vsheet-value">{t(`acct.level.${account?.myLevel || 'coordinator'}`)}</div>
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginTop: 10 }}>
              {/* The same two a technician and a client now have. Every portal
                  can change its own password without waiting for an email. */}
              <button className="tp-more-row" onClick={() => setModal('password')}>
                <span>{t('acct.password')}</span><IconChevron />
              </button>
              <button className="tp-more-row" onClick={() => setModal('email')}>
                <span>{t('acct.email')}</span><IconChevron />
              </button>
              <button className="tp-more-row" onClick={() => goTo('screen-privacy')}>
                <span>{t('legal.privacy')}</span><IconChevron />
              </button>
              <button className="tp-more-row" onClick={() => goTo('screen-terms')}>
                <span>{t('legal.terms')}</span><IconChevron />
              </button>
              <button className="tp-more-row" onClick={handleLogout}>
                <span style={{ color: 'var(--red)' }}>{t('nav.logout')}</span>
              </button>
            </div>

            <div style={{ padding: '18px 16px 24px', fontSize: 12, color: 'var(--muted)' }}>
              {currentUser?.name} · {currentUser?.email}
            </div>
          </>
        )}
      </div>

      {/* ── Bottom tab bar ───────────────────────────────────── */}
      <div className="tp-bar" role="tablist">
        {TABS.map(({ key, label, Icon, dot }) => {
          const active = accountView === key;
          return (
            <button key={key} role="tab" aria-selected={active} className={active ? 'active' : ''}
              onClick={() => setAccountView(key)}>
              <Icon size={21} />
              <span className="tp-bar-l">{label}</span>
              {dot && <span className="tp-bar-dot" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// One property: its details, its units, and every job ever done there.
//
// The repair history is the reason a manager opens a property at all — proving
// to an owner what has been spent on a building is most of what their week is.
export function PropertyDetail() {
  const {
    currentScreen, goTo, account, selectedProperty, propertyHistory,
    openPropertyForm, openUnitForm, openAccountWorkOrder, removeProperty,
    addPropertyDocuments, removePropertyDocument,
  } = useApp();
  const { t } = useI18n();
  const p = selectedProperty;
  const canEdit = ['owner', 'manager'].includes(account?.myLevel);

  const field = (label, value) => (
    <div className="vsheet-row" key={label}>
      <div className="vsheet-label">{label}</div>
      <div className={`vsheet-value ${value ? '' : 'empty'}`}>{value || '—'}</div>
    </div>
  );

  return (
    <div className={`screen ${currentScreen === 'screen-property-detail' ? 'active' : ''}`}>
      <nav className="nav">
        <BrandMark dark />
        <button className="btn btn-ghost btn-sm" onClick={() => goTo('screen-account-dashboard')}>{t('nav.back')}</button>
      </nav>

      {p && (
        <div className="tp-body" style={{ padding: '18px 16px 40px' }}>
          <div className="section-title">{p.addressLine1}{p.addressLine2 ? ` ${p.addressLine2}` : ''}</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 18 }}>
            {[p.label, [p.city, p.state].filter(Boolean).join(', '), p.zip].filter(Boolean).join(' · ')}
          </div>

          <button className="btn btn-primary btn-lg" onClick={() => openAccountWorkOrder(p)}>
            {t('acct.newServiceRequestHere')}
          </button>

          <div className="sec-label" style={{ marginTop: 24 }}>{t('acct.theBasics')}</div>
          <div className="vsheet" style={{ padding: 0 }}>
            {field(t('acct.propertyType'), t(`acct.type.${p.propertyType}`, p.propertyType))}
            {field(t('acct.externalId'), p.externalId)}
            {field(t('acct.nteAmount'), p.nteAmount != null ? fmtMoney(p.nteAmount) : t('acct.usesCompanyDefault'))}
          </div>

          <div className="sec-label" style={{ marginTop: 24 }}>{t('acct.accessSite')}</div>
          <div className="vsheet" style={{ padding: 0 }}>
            {field(t('acct.gateCode'), p.access?.gateCode)}
            {field(t('acct.lockboxCode'), p.access?.lockboxCode)}
            {field(t('acct.pets'), p.access?.pets)}
            {field(t('acct.parking'), p.access?.parkingNotes)}
            {field(t('acct.hoa'), p.access?.hoaName)}
            {field(t('acct.waterShutoff'), p.access?.waterShutoff)}
            {field(t('acct.breakerPanel'), p.access?.breakerPanel)}
          </div>

          <div className="sec-label" style={{ marginTop: 24 }}>{t('acct.buildingDetails')}</div>
          <div className="vsheet" style={{ padding: 0 }}>
            {field(t('acct.beds'), p.building?.beds)}
            {field(t('acct.baths'), p.building?.baths)}
            {field(t('acct.sqft'), p.building?.sqft)}
            {field(t('acct.yearBuilt'), p.building?.yearBuilt)}
            {field(t('acct.furnace'), [p.building?.furnaceType, p.building?.furnaceAge ? `${p.building.furnaceAge} yr` : ''].filter(Boolean).join(' · '))}
            {field(t('acct.ac'), [p.building?.acType, p.building?.acAge ? `${p.building.acAge} yr` : ''].filter(Boolean).join(' · '))}
            {field(t('acct.waterHeater'), p.building?.waterHeater)}
            {field(t('acct.roofAge'), p.building?.roofAge ? `${p.building.roofAge} yr` : '')}
          </div>

          <div className="sec-label" style={{ marginTop: 24 }}>{t('acct.ownerBilling')}</div>
          <div className="vsheet" style={{ padding: 0 }}>
            {field(t('acct.ownerName'), p.owner?.name)}
            {field(t('acct.ownerPhone'), p.owner?.phone)}
            {field(t('acct.ownerEmail'), p.owner?.email)}
            {field(t('acct.billingCode'), p.owner?.billingCode)}
          </div>

          <div className="sec-label" style={{ marginTop: 24 }}>{t('acct.units')}</div>
          {(p.units || []).filter((u) => u.isActive !== false).length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--muted)', padding: '4px 0 8px' }}>{t('acct.noUnits')}</div>
          ) : (
            <div className="tp-rows">
              {(p.units || []).filter((u) => u.isActive !== false).map((u) => (
                <button key={u._id} className="tp-row" onClick={() => canEdit && openUnitForm(p, u)}
                  disabled={!canEdit} style={!canEdit ? { cursor: 'default' } : undefined}>
                  <div className="tp-row-main">
                    <div className="tp-row-name">{u.unitNumber ? `#${u.unitNumber}` : t('acct.wholeProperty')}</div>
                    <div className="tp-row-meta">
                      {[u.tenantName, u.tenantPhone].filter(Boolean).join(' · ') || t('acct.noTenantOnFile')}
                    </div>
                  </div>
                  <div className="tp-row-right">
                    <span className={`tag tag-${u.occupancy === 'occupied' ? 'green' : u.occupancy === 'turnover' ? 'amber' : 'slate'}`}>
                      {t(`acct.occupancy.${u.occupancy || 'occupied'}`)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {canEdit && (
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn btn-outline" onClick={() => openPropertyForm(p)}>{t('acct.editProperty')}</button>
              <button className="btn btn-outline" onClick={() => openUnitForm(p, null)}>{t('acct.addUnit')}</button>
            </div>
          )}

          {/* Inspections, leases, warranties. Everyone on the account can add
              one — filing the inspection somebody just emailed you is exactly
              a coordinator's job, and it commits no money. */}
          <div style={{ marginTop: 28 }}>
            <DocumentList
              title={t('doc.documents')}
              documents={p.documents || []}
              units={(p.units || []).filter(u => u.isActive !== false)}
              onAdd={addPropertyDocuments}
              onRemove={canEdit ? removePropertyDocument : undefined}
              emptyText={t('doc.noneProperty')}
            />
          </div>

          <div className="sec-label" style={{ marginTop: 28 }}>{t('acct.repairHistory')}</div>
          {(propertyHistory || []).length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--muted)', padding: '4px 0' }}>{t('acct.noHistory')}</div>
          ) : (
            <div className="tp-rows">
              {(propertyHistory || []).map((h) => (
                <div key={h._id} className="tp-row" style={{ cursor: 'default' }}>
                  <div className="tp-row-main">
                    <div className="tp-row-name">{h.serviceType || h.workOrder?.number || t('acct.workOrder')}</div>
                    <div className="tp-row-meta">{fmtDate(h.createdAt)}{h.unitLabel ? ` · ${h.unitLabel}` : ''}</div>
                  </div>
                  <div className="tp-row-right">
                    {h.invoice?.amount != null && <div className="tp-row-amt">{fmtMoney(h.invoice.amount)}</div>}
                    <span className={`tp-chip tp-chip-${STATUS_TONE[h.status] || 'slate'}`}>{t(`status.${h.status}`, h.status)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {canEdit && (
            <button className="btn btn-ghost" style={{ marginTop: 28, color: 'var(--red)' }} onClick={() => removeProperty(p)}>
              {t('acct.removeProperty')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
